import React, { useEffect, useState } from 'react';

import { ProgramTaskStatus, States } from '@vestahealthcare/common/enums';
import Enum, { Selectable } from '@vestahealthcare/common/enums/Enum';
import { translate } from '@vestahealthcare/common/i18n';
import { PodRule } from '@vestahealthcare/common/models';

import { Panel, Select, SelectLanguage } from 'styleguide-v2';
import { FilterGroup, FilterItem } from 'styleguide-v2/src/components/Panel';

import { GetProgramTaskParam } from 'dash/src/services/PodServices';
import { getFilterCount, getFilterItem } from 'dash/src/utils/filterUtils';

type KeyGetProgramTaskParams = keyof GetProgramTaskParam;

type Props = {
  data: {
    appointmentTypes: Selectable[];
    employees: Selectable[];
    languages: Selectable[];
    taskDefinitions: Selectable[];
    touchpointReasons: Selectable[];
  };
  filters?: {
    [x in KeyGetProgramTaskParams]?: FilterItem;
  };
  loadingFilters?: boolean;
  open: boolean;
  onChange: (
    filters: {
      [x in KeyGetProgramTaskParams]?: FilterItem;
    },
  ) => void;
  onClose: () => void;
};

const MEMBER_FILTERS: KeyGetProgramTaskParams[] = ['state', 'language'];

const TASK_FILTERS: KeyGetProgramTaskParams[] = [
  'assigneeId',
  'statusId',
  'lastAppointmentType',
  'nextAppointmentType',
  'touchpointReason',
];

export const ForecastDetailTaskFilterModal = ({
  data,
  filters,
  loadingFilters,
  open,
  onChange,
  onClose,
}: Props) => {
  const [pendingFilters, setPendingFilters] = useState<
    {
      [x in KeyGetProgramTaskParams]?: FilterItem;
    }
  >(filters || {});

  useEffect(() => {
    setPendingFilters(filters || {});
  }, [filters]);

  return (
    <Panel.FilterModal
      open={open}
      onClear={() => {
        onChange({});
      }}
      onClose={onClose}
      onSubmit={() => onChange(pendingFilters)}
    >
      <FilterGroup
        title="Member Details"
        count={getFilterCount(pendingFilters, MEMBER_FILTERS)}
        open
      >
        <SelectLanguage
          data-cy="pod-forecast-filter-modal-state"
          items={data.languages}
          label={translate('pods.metrics.forecastDetail.filter.language')}
          limitTags={1}
          loading={loadingFilters}
          onChange={(values: Selectable[]) => {
            setPendingFilters({
              ...pendingFilters,
              language: getFilterItem(
                translate('pods.metrics.forecastDetail.filter.language'),
                values,
              ),
            });
          }}
          size="small"
          value={(pendingFilters.language?.value as Selectable[]) || []}
        />
        <Select
          data-cy="pod-forecast-filter-modal-state"
          items={Enum.toSelectable(States.asArray)}
          label={translate('pods.metrics.forecastDetail.filter.state')}
          limitTags={1}
          multiple
          onChange={(values: Selectable[]) => {
            setPendingFilters({
              ...pendingFilters,
              state: getFilterItem(
                translate('pods.metrics.forecastDetail.filter.state'),
                values,
              ),
            });
          }}
          size="small"
          value={pendingFilters.state?.value || []}
        />
      </FilterGroup>
      <FilterGroup
        title="Task Details"
        count={getFilterCount(pendingFilters, TASK_FILTERS)}
        open
      >
        <Select
          data-cy="pod-forecast-filter-modal-assignee"
          items={data.employees}
          label={translate('pods.metrics.forecastDetail.filter.assignee')}
          limitTags={1}
          loading={loadingFilters}
          multiple
          onChange={(values: Selectable[]) => {
            setPendingFilters({
              ...pendingFilters,
              assigneeId: getFilterItem(
                translate('pods.metrics.forecastDetail.filter.assignee'),
                values,
              ),
            });
          }}
          size="small"
          value={pendingFilters.assigneeId?.value || []}
        />
        <Select
          data-cy="pod-forecast-filter-modal-status"
          items={Enum.toSelectable(ProgramTaskStatus.asArray)}
          label={translate('pods.metrics.forecastDetail.filter.status')}
          limitTags={1}
          multiple
          onChange={(values: Selectable[]) => {
            setPendingFilters({
              ...pendingFilters,
              statusId: getFilterItem(
                translate('pods.metrics.forecastDetail.filter.status'),
                values,
              ),
            });
          }}
          size="small"
          value={pendingFilters.statusId?.value || []}
        />
        <Select
          data-cy="pod-forecast-filter-modal-touchpointReason"
          items={data.touchpointReasons}
          label={translate(
            'pods.metrics.forecastDetail.filter.touchpointReason',
          )}
          limitTags={1}
          loading={loadingFilters}
          multiple
          onChange={(values: Selectable[]) => {
            setPendingFilters({
              ...pendingFilters,
              touchpointReason: getFilterItem(
                translate(
                  'pods.metrics.forecastDetail.filter.touchpointReason',
                ),
                values,
              ),
            });
          }}
          size="small"
          value={pendingFilters.touchpointReason?.value || []}
        />
        <Select
          data-cy="pod-forecast-filter-modal-lastAppointmentType"
          items={data.appointmentTypes}
          label={translate(
            'pods.metrics.forecastDetail.filter.lastAppointmentType',
          )}
          limitTags={1}
          loading={loadingFilters}
          multiple
          onChange={(values: Selectable[]) => {
            setPendingFilters({
              ...pendingFilters,
              lastAppointmentType: getFilterItem(
                translate(
                  'pods.metrics.forecastDetail.filter.lastAppointmentType',
                ),
                values,
              ),
            });
          }}
          size="small"
          value={pendingFilters.lastAppointmentType?.value || []}
        />
        <Select
          data-cy="pod-forecast-filter-modal-nextAppointmentType"
          items={data.appointmentTypes}
          label={translate(
            'pods.metrics.forecastDetail.filter.nextAppointmentType',
          )}
          limitTags={1}
          loading={loadingFilters}
          multiple
          onChange={(values: Selectable[]) => {
            setPendingFilters({
              ...pendingFilters,
              nextAppointmentType: getFilterItem(
                translate(
                  'pods.metrics.forecastDetail.filter.nextAppointmentType',
                ),
                values,
              ),
            });
          }}
          size="small"
          value={pendingFilters.nextAppointmentType?.value || []}
        />
        <Select
          data-cy="pod-forecast-filter-modal-subtype"
          items={data.taskDefinitions.filter(({ value }) =>
            PodRule.CHILD_TYPES.includes(value as number),
          )}
          label={translate(
            'pods.metrics.forecastDetail.filter.childTaskDefinitionId',
          )}
          limitTags={1}
          loading={loadingFilters}
          multiple
          onChange={(values: Selectable[]) => {
            setPendingFilters({
              ...pendingFilters,
              childTaskDefinitionId: getFilterItem(
                translate(
                  'pods.metrics.forecastDetail.filter.childTaskDefinitionId',
                ),
                values,
              ),
            });
          }}
          size="small"
          value={pendingFilters.childTaskDefinitionId?.value || []}
        />
      </FilterGroup>
    </Panel.FilterModal>
  );
};
